<template>
  <div class="h-full">
    <VVerificationRequiredOverlay />
    <VLowEnvelopeAllowanceWarning />
    <VLoadingSpinner
      v-if="requestsInProgress > 0"
    />
    <div
      v-else
      class="g-page p-new-envelope">

      <VDocumentSelectionForm
        v-if="step === 0"
        @nextStep="nextStep"
      />
      <VDocumentSelectionConfirmation
        v-if="step > 0"
      />
      <h3 v-if="canShowPartySelectionScreen" class="mt-lg">{{ $t('createEnvelope.sharing.sharingSettings') }}</h3>
      <EnvelopeSharing
        v-if="canShowPartySelectionScreen"
        class="lg:w-1/2 mt-base"
        @interface="handleEnvelopeSharingInterface">
        <template v-slot:header>
          <h3>{{ $t('createEnvelope.sharing.sharing') }}</h3>
        </template>
      </EnvelopeSharing>

      <div v-if="step > 0">

        <hr />

        <SigningSequenceSelector
          v-if="canShowPartySelectionScreen"
          :current-selection="currentSigningSequence"
          @selectionUpdated="updateSigningSequence"
          class="py-base"
        />

        <SigningSequenceEditor
          v-else
          :current-selection="currentSigningSequence"
          @selectionUpdated="updateSigningSequence"
          class="pb-base"
        />
      </div>

      <hr />

      <VPartySelectionForm
        v-if="canShowPartySelectionScreen"
        @nextStep="handlePartyNextStep"
        class="pt-base"
      />
      <VPartySelectionConfirmation
        v-if="step > 1"
      />

      <VMergeFieldForm
        v-if="step === 2"
        @nextStep="nextStep"
      />
      <VMergeFieldConfirmation
        v-if="step > 2 && envelopeHasMergeFields"
      />

      <VSendForm
        v-if="step === 3"
      />
      <VMessage
        v-if="!haveDocumentsBeenProcessed && step > 0"
        class="p-message"
        type="warning"
        :fullwidth="true">
        <template v-slot:icon>
          <ExclamationTriangleIcon />
        </template>
        <template v-slot:text>
          {{ $t('envelope.processingDocuments') }}.
          <i18n path="envelope.helpMessage">
            <template v-slot:link>
              <a class="helpMessageLink" href="https://signable.help/why-does-my-pdf-document-not-process-in-signable/" target="_blank">{{ $t('envelope.helpMessageSuggestion') }}</a>
            </template>
          </i18n>
        </template>
      </VMessage>

      <!--
        commenting out and referencing as "REMOVEME", not deleting until we know
        more about what we're gonna do about this.

        The spaces between the html comments are to ensure that they aren't
        misunderstood or misreferenced.

        If removed in the future, references to to `VDocumentSetupProgress` should
        also be removed.
      -->
      <!-- <div class="p-new-envelope__progress" :class="{ 'footer-feedback': $parent.showFeedback }">
        < !-- <div class="g-page__footer__left"> -- >
          <VDocumentSetupProgress :icons="[
            { name: 'file', description: $t('createEnvelope.documentSetupProgress.selectYourDocuments') },
            { name: 'users', description: $t('createEnvelope.documentSetupProgress.addYourParites') },
            { name: 'list-ol', description: $t('createEnvelope.documentSetupProgress.addYourFields') }
          ]" :step="step" />
        < !-- </div> -- >
      </div> -- >
      <! -- end of commenting out of "REMOVEME" -->
    </div>
  </div>
</template>

<script>
import VDocumentSelectionForm from '@/views/envelope-create/DocumentSelectionForm.vue';
import VDocumentSelectionConfirmation from '@/views/envelope-create/DocumentSelectionConfirmation.vue';
import EnvelopeSharing from '@/components/EnvelopeSharing.vue';
import VPartySelectionForm from '@/views/envelope-create/PartySelectionForm.vue';
import VPartySelectionConfirmation from '@/views/envelope-create/PartySelectionConfirmation.vue';
import VMergeFieldForm from '@/views/envelope-create/MergeFieldForm.vue';
import VMergeFieldConfirmation from '@/views/envelope-create/MergeFieldConfirmation.vue';
import VSendForm from '@/views/envelope-create/SendForm.vue';
// import VDocumentSetupProgress from '@/views/components/DocumentSetupProgress.vue';
import VLoadingSpinner from '@/components/deprecated/atoms/LoadingSpinner.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';

import VLowEnvelopeAllowanceWarning from '@/views/components/LowEnvelopeAllowanceWarning.vue';
import VVerificationRequiredOverlay from '@/views/components/VerificationRequiredOverlay.vue';

import SigningSequenceSelector from '@/components/ui/signingSequence/SigningSequenceSelector.vue';
import SigningSequenceEditor from '@/components/ui/signingSequence/SigningSequenceEditor.vue';
import { mapActions, mapGetters } from 'vuex';
import { ExclamationTriangleIcon } from '@/components/icons/fontIcons';

export default {
  name: 'EnvelopeCreate',
  description: 'Orchestrator for the whole Envelope send process',
  components: {
    EnvelopeSharing,
    SigningSequenceSelector,
    SigningSequenceEditor,
    VMessage,
    VLoadingSpinner,
    VDocumentSelectionForm,
    VDocumentSelectionConfirmation,
    VPartySelectionForm,
    VPartySelectionConfirmation,
    VMergeFieldForm,
    VMergeFieldConfirmation,
    VSendForm,
    // VDocumentSetupProgress,
    VLowEnvelopeAllowanceWarning,
    VVerificationRequiredOverlay,
    ExclamationTriangleIcon,
  },
  data() {
    return {
      requestsInProgress: 0,
      shareWithEveryone: false,
      step: 0,
      envelopeSharingInterface: null,
    };
  },
  async created() {
    this.requestsInProgress += 2;

    await Promise.all([
      this.fetchTemplates(),
      this.fetchEnvelope(),
    ]);

    if (!this.isInitialOrDraft) {
      this.$router.replace({
        name: 'EnvelopeStatus',
        params: { envelopeFingerprint: this.$route.params.envelopeFingerprint },
      });
    }

    this.requestsInProgress -= 2;
  },
  computed: {
    envelopeHasMergeFields() {
      return !!Object.keys(this.$store.getters['envelopes/mergeFieldTags']).length;
    },
    ...mapGetters({
      plan: 'plan/permissions',
      planHasFeature: 'plan/planHasFeature',
      company: 'company/company',
      userHasRole: 'roles/userHasRole',
      currentEnvelope: 'envelopes/current',
      currentSigningSequence: 'envelopes/currentSigningSequence',
      haveDocumentsBeenProcessed: 'envelopes/processedDocuments',
    }),
    isInitialOrDraft() {
      return ['initial', 'draft'].includes(this.currentEnvelope.status);
    },
    canShowPartySelectionScreen() {
      return this.step === 1 && this.haveDocumentsBeenProcessed;
    },
  },
  methods: {
    ...mapActions({
      updateEnvelope: 'envelopes/UPDATE_ENVELOPE',
      setSigningSequence: 'envelopes/SET_SIGNING_SEQUENCE',
    }),
    async updateSigningSequence(value) {
      try {
        await this.setSigningSequence(value);
      } catch (e) {
        const translationKey = e.failedValue ? `signingSequence.messages.update_error_${e.failedValue}` : 'signingSequence.messages.update_error';
        this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t(translationKey),
          icon: 'times',
          type: 'error',
        }));
      }
    },
    handlePartyNextStep() {
      this.requestsInProgress += 1;
      this.envelopeSharingInterface.save().then(() => {
        this.requestsInProgress -= 1;
        this.step += 1;
      });
    },
    nextStep() {
      this.step += 1;
    },
    handleEnvelopeSharingInterface(envelopeSharingInterface) {
      this.envelopeSharingInterface = envelopeSharingInterface;
    },
    async fetchEnvelope() {
      await this.$store.dispatch('envelopes/GET_ENVELOPE', {
        fingerprint: this.$route.params.envelopeFingerprint,
        isCreating: true,
      });
      this.step = this.$store.getters['envelopes/creationStep'];
    },
    async fetchTemplates() {
      try {
        await this.$store.dispatch('templates/PAGINATE', {
          page: 1,
          pageLimit: 9,
        });
      } catch (error) {
        if (!error.response) throw error;

        this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.fail.fetch', { name: 'templates' }),
          icon: 'times',
          type: 'error',
        }));
      }
    },
  },
};
</script>

<style scoped>
.footer-feedback {
  bottom: 48px;
}

.p-new-envelope {
  padding-bottom: 103px;
}

.p-new-envelope__progress {
  position: fixed;
  bottom: 0;
  height: auto;
  background: white;
  padding: 15px;
  border: 1px solid #dde2e5;
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
  box-shadow: 0px 1px 10px 0px rgba(0, 0, 0, 0.05);
  z-index: 2;
}

.p-message {
  margin: var(--spacing-large) auto;
  display: block;
  width: 50%;
}
.helpMessageLink {
    color: white;
}

@media( max-width: 478px ) {
  .p-new-envelope__progress {
    display: none;
  }
}
</style>
